import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
    ArrowDownLeft,
    ArrowUpRight,
    ClipboardCheck,
    FileText,
    Image as ImageIcon,
    Inbox,
    MessageCircle,
    Mic,
    Wifi,
    WifiOff,
} from "lucide-react";
import { toast } from "sonner";
import { whatsappMerkez } from "@/lib/api";
import useWebSocket from "@/lib/useWebSocket";

const WA_HUB = "/yeni/iletisim-merkezi/whatsapp";
const TOAST_DEDUPE_MS = 8000;

const KATEGORI = {
    gelen: { label: "Gelen", icon: ArrowDownLeft, tone: "text-emerald-400 bg-emerald-500/10" },
    gonderim: { label: "Gönderim", icon: ArrowUpRight, tone: "text-sky-400 bg-sky-500/10" },
    onay: { label: "Onay", icon: ClipboardCheck, tone: "text-amber-400 bg-amber-500/10" },
    hata: { label: "Hata", icon: ClipboardCheck, tone: "text-rose-400 bg-rose-500/10" },
};

const FILTERS = [
    { id: "", label: "Tümü" },
    { id: "gelen", label: "Gelen" },
    { id: "gonderim", label: "Gönderim" },
    { id: "onay", label: "Onay" },
];

const TUR_ICON = {
    evrak: FileText,
    fotoğraf: ImageIcon,
    foto: ImageIcon,
    ses: Mic,
    mesaj: MessageCircle,
};

function fmtTime(iso) {
    if (!iso) return "şimdi";
    try {
        const d = new Date(iso);
        const diff = Date.now() - d.getTime();
        if (diff < 60000) return "şimdi";
        return d.toLocaleString("tr-TR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "";
    }
}

function resolveHref(n) {
    if (n?.href) return n.href.startsWith("/") ? n.href : `/${n.href}`;
    const tel = n?.meta?.konusma_anahtar || n?.meta?.telefon || n?.konusma_anahtar;
    if (tel) return `${WA_HUB}?tab=gelen&k=${encodeURIComponent(tel)}`;
    return `${WA_HUB}?tab=gelen`;
}

function normalizeBell(raw) {
    if (!raw) return null;
    const meta = raw.meta || {};
    return {
        id: String(raw.id || raw.inbox_id || `live-${Date.now()}`),
        kategori: raw.kategori || meta.kategori || "gelen",
        title: raw.title || "WhatsApp bildirimi",
        message: raw.message || "",
        href: raw.href || resolveHref(raw),
        gonderen: raw.gonderen || meta.gonderen || "",
        tur: (raw.tur || meta.tur || "").toLowerCase(),
        created_at: raw.created_at || new Date().toISOString(),
        meta: { ...meta, konusma_anahtar: raw.konusma_anahtar || meta.konusma_anahtar },
        dosya_no: raw.dosya_no || null,
        _live: Boolean(raw._live),
    };
}

function parseInboxWs(data) {
    const p = data?.payload || {};
    const bell = p.bell ? normalizeBell({ ...p.bell, _live: true }) : null;
    if (bell) return bell;

    const msg = p.mesaj && typeof p.mesaj === "object" ? p.mesaj : null;
    if (!msg && !p.konusma_anahtar) return null;

    const gonderen = msg?.gorunen_baslik || msg?.gonderen_adi || msg?.gonderen_etiket || "Bilinmeyen";
    const media = (msg?.media_turu || "").toLowerCase();
    const text = (msg?.mesaj || "").trim();
    let tur = "Mesaj";
    let detay = text || "Yeni içerik";

    if (media.includes("document") || media === "application") {
        tur = "Evrak";
        detay = text || "Belge / PDF gönderdi";
    } else if (media.includes("image")) {
        tur = "Fotoğraf";
        detay = text || "Görsel gönderdi";
    } else if (media.includes("video")) {
        tur = "Video";
        detay = text || "Video gönderdi";
    } else if (media.includes("audio") || media.includes("ptt")) {
        tur = "Ses kaydı";
        detay = text || "Sesli mesaj gönderdi";
    }

    const key = p.konusma_anahtar || msg?.konusma_anahtar || msg?.telefon;

    return normalizeBell({
        id: `inbox-${p.inbox_id || msg?.id || key}`,
        kategori: "gelen",
        title: `${gonderen} — ${tur} gönderdi`,
        message: detay,
        gonderen,
        tur,
        konusma_anahtar: key,
        href: key ? `${WA_HUB}?tab=gelen&k=${encodeURIComponent(key)}` : `${WA_HUB}?tab=gelen`,
        _live: true,
    });
}

export default function WhatsAppNotificationCenter() {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState({
        inbox_okunmamis: 0,
        inbox_onay_bekleyen: 0,
        kuyruk_onay: 0,
    });
    const [filter, setFilter] = useState("");
    const [liveFlash, setLiveFlash] = useState(false);
    const btnRef = useRef(null);
    const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
    const lastTs = useRef(null);
    const toastDedupe = useRef(new Map());
    const bootstrapped = useRef(false);

    const pushItem = useCallback((raw, { toast: showToastFlag = true } = {}) => {
        const n = normalizeBell(raw);
        if (!n) return;

        setItems((prev) => {
            const merged = [n, ...prev.filter((x) => String(x.id) !== String(n.id))];
            return merged.slice(0, 80);
        });

        if (showToastFlag) {
            const dedupeKey = `${n.kategori}:${n.meta?.inbox_id || n.id}:${n.message?.slice(0, 40)}`;
            const last = toastDedupe.current.get(dedupeKey) || 0;
            if (Date.now() - last > TOAST_DEDUPE_MS) {
                toastDedupe.current.set(dedupeKey, Date.now());
                const TurIcon = TUR_ICON[n.tur] || MessageCircle;
                toast(n.title, {
                    description: n.message || n.gonderen,
                    icon: <TurIcon className="h-4 w-4 text-emerald-400" />,
                    duration: 9000,
                    action: {
                        label: "Aç",
                        onClick: () => {
                            window.location.href = resolveHref(n);
                        },
                    },
                });
                setLiveFlash(true);
                setTimeout(() => setLiveFlash(false), 1200);
            }
        }

        if (n.kategori === "gelen") {
            setStats((s) => ({ ...s, inbox_okunmamis: (s.inbox_okunmamis || 0) + 1 }));
        }
    }, []);

    const onNotifyWs = useCallback(
        (data) => {
            if (!data) return;
            const raw = data.notification || null;
            if (!raw || raw.event_type !== "whatsapp_sent") return;
            pushItem(
                {
                    ...raw,
                    kategori: raw.meta?.kategori || (raw.title?.toLowerCase().includes("gelen") ? "gelen" : "gonderim"),
                    gonderen: raw.meta?.gonderen,
                    tur: raw.meta?.tur,
                    href: resolveHref(raw),
                    _live: true,
                },
                { toast: true },
            );
        },
        [pushItem],
    );

    const onInboxWs = useCallback(
        (data) => {
            if (!data || data.type === "connected") return;

            if (data.type === "wa_bell") {
                pushItem({ ...data.payload, _live: true }, { toast: true });
                return;
            }

            if (data.type === "inbox_message") {
                const parsed = parseInboxWs(data);
                if (parsed) pushItem(parsed, { toast: true });
            }
        },
        [pushItem],
    );

    const { connected: notifyWs } = useWebSocket("notifications/", onNotifyWs);
    const { connected: inboxWs } = useWebSocket("wa-inbox/", onInboxWs);
    const connected = notifyWs || inboxWs;

    const poll = useCallback(async () => {
        try {
            const data = await whatsappMerkez.bildirimKutusu(lastTs.current, filter || undefined);
            if (data.stats) setStats(data.stats);
            const fresh = (data.items || []).map((x) => normalizeBell(x)).filter(Boolean);

            if (fresh.length) {
                lastTs.current = data.server_time || fresh[0]?.created_at;
            }

            if (!bootstrapped.current) {
                bootstrapped.current = true;
                if (fresh.length) setItems(fresh.slice(0, 80));
                return;
            }

            if (fresh.length) {
                setItems((prev) => {
                    const merged = [...fresh, ...prev];
                    const byId = new Map();
                    merged.forEach((x) => byId.set(String(x.id), x));
                    return Array.from(byId.values()).slice(0, 80);
                });
            }
        } catch {
            /* sessiz */
        }
    }, [filter]);

    useEffect(() => {
        poll();
        const ms = connected ? 12000 : 4000;
        const id = setInterval(poll, ms);
        return () => clearInterval(id);
    }, [poll, connected]);

    useEffect(() => {
        bootstrapped.current = false;
        lastTs.current = null;
        poll();
    }, [filter, poll]);

    useEffect(() => {
        if (!open || !btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        const panelW = 420;
        const panelH = Math.min(window.innerHeight * 0.78, 680);
        let left = rect.right - panelW;
        let top = rect.top - panelH - 8;
        if (left < 8) left = 8;
        if (top < 8) top = rect.bottom + 8;
        if (left + panelW > window.innerWidth - 8) {
            left = window.innerWidth - panelW - 8;
        }
        setPanelPos({ top, left });
    }, [open]);

    async function removeAll() {
        try {
            await whatsappMerkez.bildirimKutusuTemizle();
            setItems([]);
            toast.success("WhatsApp bildirimleri temizlendi");
        } catch {
            toast.error("Temizlenemedi");
        }
    }

    const badgeCount =
        (stats.inbox_okunmamis || 0) +
        (stats.inbox_onay_bekleyen || 0) +
        (stats.kuyruk_onay || 0);

    const filteredItems = filter
        ? items.filter((n) => (n.kategori || "gonderim") === filter)
        : items;

    const panel =
        open &&
        createPortal(
            <>
                <div className="fixed inset-0 z-[200]" onClick={() => setOpen(false)} aria-hidden />
                <div
                    role="dialog"
                    aria-label="WhatsApp bildirimleri"
                    className="fixed z-[210] w-[min(420px,calc(100vw-16px))] max-h-[78vh] flex flex-col rounded-xl border border-emerald-500/20 bg-[#071210] shadow-2xl shadow-emerald-900/30"
                    style={{ top: panelPos.top, left: panelPos.left }}
                >
                    <div className="px-4 py-3 border-b border-emerald-500/15 shrink-0">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                                <div className="h-8 w-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                                    <MessageCircle className="h-4 w-4 text-emerald-400" />
                                </div>
                                <div>
                                    <div className="text-sm font-semibold text-white">WhatsApp Bildirimleri</div>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                        {connected ? (
                                            <>
                                                <Wifi className="h-3 w-3 text-emerald-400" />
                                                <span className="text-[10px] text-emerald-400">Anlık · canlı push</span>
                                            </>
                                        ) : (
                                            <>
                                                <WifiOff className="h-3 w-3 text-amber-400" />
                                                <span className="text-[10px] text-amber-400">Bağlanıyor…</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {items.length > 0 && (
                                <button
                                    type="button"
                                    onClick={removeAll}
                                    className="text-[11px] text-rose-400 hover:text-rose-300 shrink-0"
                                >
                                    Temizle
                                </button>
                            )}
                        </div>

                        <div className="grid grid-cols-3 gap-2 mt-3">
                            <Link
                                to={`${WA_HUB}?tab=gelen`}
                                onClick={() => setOpen(false)}
                                className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-2 py-1.5 text-center hover:bg-emerald-500/15"
                            >
                                <div className="text-lg font-bold text-emerald-300">{stats.inbox_okunmamis || 0}</div>
                                <div className="text-[10px] text-emerald-400/80">Okunmamış</div>
                            </Link>
                            <Link
                                to={`${WA_HUB}?tab=gelen&onay=1`}
                                onClick={() => setOpen(false)}
                                className="rounded-lg bg-amber-500/10 border border-amber-500/20 px-2 py-1.5 text-center hover:bg-amber-500/15"
                            >
                                <div className="text-lg font-bold text-amber-300">{stats.inbox_onay_bekleyen || 0}</div>
                                <div className="text-[10px] text-amber-400/80">Evrak onay</div>
                            </Link>
                            <Link
                                to={`${WA_HUB}?tab=kuyruk`}
                                onClick={() => setOpen(false)}
                                className="rounded-lg bg-sky-500/10 border border-sky-500/20 px-2 py-1.5 text-center hover:bg-sky-500/15"
                            >
                                <div className="text-lg font-bold text-sky-300">{stats.kuyruk_onay || 0}</div>
                                <div className="text-[10px] text-sky-400/80">Kuyruk onay</div>
                            </Link>
                        </div>

                        <div className="flex gap-1 mt-3 flex-wrap">
                            {FILTERS.map((f) => (
                                <button
                                    key={f.id || "all"}
                                    type="button"
                                    onClick={() => setFilter(f.id)}
                                    className={`text-[10px] px-2 py-1 rounded-full border transition ${
                                        filter === f.id
                                            ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-200"
                                            : "border-white/10 text-slate-400 hover:text-white"
                                    }`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="overflow-y-auto flex-1 min-h-0">
                        {filteredItems.length === 0 ? (
                            <div className="p-6 text-sm text-slate-500 text-center leading-relaxed">
                                <Inbox className="h-8 w-8 mx-auto mb-2 text-emerald-500/40" />
                                WhatsApp bildirimi yok.
                                <br />
                                <span className="text-[11px]">Gelen mesaj ve evraklar anlık burada görünür.</span>
                            </div>
                        ) : (
                            <ul className="divide-y divide-emerald-500/[0.06]">
                                {filteredItems.map((n) => {
                                    const kat = KATEGORI[n.kategori] || KATEGORI.gonderim;
                                    const Icon = TUR_ICON[n.tur] || kat.icon;
                                    return (
                                        <li key={n.id}>
                                            <Link
                                                to={resolveHref(n)}
                                                onClick={() => setOpen(false)}
                                                className={`flex gap-3 p-4 hover:bg-emerald-500/[0.04] transition-colors ${
                                                    n._live ? "bg-emerald-500/[0.06]" : ""
                                                }`}
                                            >
                                                <div className={`shrink-0 h-9 w-9 rounded-lg flex items-center justify-center ${kat.tone}`}>
                                                    <Icon className="h-4 w-4" />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                                                        {n.gonderen ? (
                                                            <span className="text-xs font-semibold text-emerald-200 truncate max-w-[200px]">
                                                                {n.gonderen}
                                                            </span>
                                                        ) : null}
                                                        {n.tur ? (
                                                            <span className="text-[10px] uppercase tracking-wider text-slate-500 bg-white/5 px-1.5 py-0.5 rounded">
                                                                {n.tur}
                                                            </span>
                                                        ) : null}
                                                        {n._live ? (
                                                            <span className="text-[9px] text-emerald-400 font-bold">CANLI</span>
                                                        ) : null}
                                                        <span className="text-[10px] text-slate-600 ml-auto shrink-0">
                                                            {fmtTime(n.created_at)}
                                                        </span>
                                                    </div>
                                                    <div className="text-sm font-medium text-white leading-snug">{n.title}</div>
                                                    {n.message ? (
                                                        <div className="text-xs text-slate-400 mt-1 leading-relaxed line-clamp-2">
                                                            {n.message}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    <div className="px-4 py-2 border-t border-emerald-500/10 shrink-0">
                        <Link
                            to={WA_HUB}
                            onClick={() => setOpen(false)}
                            className="block text-center text-xs text-emerald-400 hover:text-emerald-300 py-1"
                        >
                            WhatsApp Merkezi → Gelen kutusu
                        </Link>
                    </div>
                </div>
            </>,
            document.body,
        );

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((v) => !v);
                }}
                className={`relative h-9 w-9 rounded-md border border-emerald-500/25 bg-emerald-500/5 hover:bg-emerald-500/10 flex items-center justify-center text-emerald-400 transition ${
                    liveFlash ? "ring-2 ring-emerald-400/60 scale-105" : ""
                }`}
                title="WhatsApp bildirimleri (anlık)"
                aria-expanded={open}
            >
                <MessageCircle className="h-4 w-4" />
                <span
                    className={`absolute bottom-0.5 right-0.5 h-1.5 w-1.5 rounded-full ${
                        connected ? "bg-emerald-500 animate-pulse" : "bg-amber-500"
                    }`}
                />
                {badgeCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-emerald-500 text-[10px] font-bold text-[#052e1a] flex items-center justify-center">
                        {badgeCount > 99 ? "99+" : badgeCount}
                    </span>
                )}
            </button>
            {panel}
        </>
    );
}
